/**
 * notification-route — pure parser that turns a server-emitted
 * `default_url` string into an SPA action descriptor.
 *
 * The notifications API gives every notification a `default_url`. We
 * don't `window.location` — the SPA owns navigation. This helper
 * parses the URL into one of:
 *
 *   - { tab, takeover?, takeoverId? }  — switch to a tab and open
 *     the corresponding take-over via a bus event
 *   - { tab }                          — switch tab only
 *   - null                             — unrecognised, fall back to
 *     dashboard root with no error (graceful degrade)
 *
 * Pattern matching is intentionally loose so a backend that adds new
 * URL shapes doesn't break the bell. New patterns = new branches here,
 * no other surface needs updating.
 * see docs/mentorship-flow.md for the cross-module SSOT routing rationale.
 */

import type { TabType } from '../navigation/tabMetadata';
import { captureError } from '../errors/captureError';

export type NotificationAction =
    | { tab: TabType; takeover: 'submission'; takeoverId: string }
    | { tab: TabType; takeover: 'report'; takeoverId: string }
    | { tab: TabType; takeover: 'project'; takeoverId: string }
    | { tab: TabType; takeover: 'thread'; takeoverId: string }
    | { tab: TabType }
    | null;

/** Extract the path part from `url` — accepts absolute URLs, paths,
 *  or hash routes. Defensive against malformed input. */
function pathOf(url: string): string {
    try {
        // Absolute URL.
        const u = new URL(url, 'http://placeholder.local');
        return u.pathname.replace(/^\/+|\/+$/g, '');
    } catch (err) {
        captureError(err, { scope: 'notifications.route', op: 'pathOf' });
        return url.replace(/^\/+|\/+$/g, '');
    }
}

/** Tail segment of a path — used to pull an id off `/foo/bar/<id>`. */
function lastSegment(path: string): string | null {
    const parts = path.split('/').filter(Boolean);
    return parts[parts.length - 1] ?? null;
}

export function parseNotificationUrl(url: string): NotificationAction {
    if (!url) return null;
    const p = pathOf(url);
    if (!p) return null;

    // Mentorship: submission review (mentor side)
    //   /mentorship/submissions/<id>
    // eslint-disable-next-line chiefy/no-naked-concept-strings -- backend URL path segments (infrastructure ids), not user copy.
    if (p.startsWith('mentorship/submissions/') || p.startsWith('api/mentorship/submissions/')) {
        const id = lastSegment(p);
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value for NotificationAction union (infrastructure id), not user copy.
        if (id) return { tab: 'mentorship', takeover: 'submission', takeoverId: id };
    }

    // Mentorship: report / feedback (mentee side)
    //   /mentorship/reports/<id>
    // eslint-disable-next-line chiefy/no-naked-concept-strings -- backend URL path segments (infrastructure ids), not user copy.
    if (p.startsWith('mentorship/reports/') || p.startsWith('api/mentorship/reports/')) {
        const id = lastSegment(p);
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value for NotificationAction union (infrastructure id), not user copy.
        if (id) return { tab: 'mentorship', takeover: 'report', takeoverId: id };
    }

    // Mentorship: project (either side)
    //   /mentorship/projects/<id>
    // eslint-disable-next-line chiefy/no-naked-concept-strings -- backend URL path segments (infrastructure ids), not user copy.
    if (p.startsWith('mentorship/projects/') || p.startsWith('api/mentorship/projects/')) {
        const id = lastSegment(p);
        // eslint-disable-next-line chiefy/no-naked-concept-strings -- discriminator value for NotificationAction union (infrastructure id), not user copy.
        if (id) return { tab: 'mentorship', takeover: 'project', takeoverId: id };
    }

    // Messaging: open a specific thread
    //   /messages/<threadId> (legacy backend URL shape preserved; the
    //   TabType discriminator is the post-slice-86 'conversations').
    if (p.startsWith('messages/') || p.startsWith('api/conversations/')) {
        const id = lastSegment(p);
        if (id) return { tab: 'conversations', takeover: 'thread', takeoverId: id };
    }

    // Top-level tab fallbacks — when the URL points at a section root
    // we just switch tabs, no take-over.
    //
    // Why: the backend `default_url` still uses the legacy '/messages/...'
    // URL segment (an infrastructure id owned by the BE notification
    // service), so we accept `messages` as an alias and route it to the
    // canonical TabType discriminator 'conversations'. The 'conversations'
    // path is accepted too in case the backend migrates.
    const top = p.split('/')[0];
    if (top === 'messages' || top === 'conversations') {
        return { tab: 'conversations' };
    }
    const knownTabs: ReadonlyArray<TabType> = [
        'home', 'assistant', 'networking', 'education', 'mentorship',
        'profile', 'settings',
    ];
    if (top && (knownTabs as ReadonlyArray<string>).includes(top)) {
        return { tab: top as TabType };
    }

    return null;
}
